import StatusIndicator from '../StatusIndicator';

export type ProcessingStep =
  | 'pages'
  | 'segmentation'
  | 'pdfParser'
  | 'metadata'
  | 'embeddings'
  | 'classification'
  | 'reconstruction'
  | 'dataExtraction'
  | 'supervisor';

const STEP_LABELS: Record<ProcessingStep, string> = {
  pages: 'Pages',
  segmentation: 'Segmentation',
  pdfParser: 'Parser',
  metadata: 'Metadata',
  embeddings: 'Embeddings',
  classification: 'Classification',
  reconstruction: 'Reconstruction',
  dataExtraction: 'Data Extraction',
  supervisor: 'Supervisor',
};

type StatusEntry = {
  status?: 'done' | 'error' | 'skip' | 'processing' | 'pending' | 'pages-done' | 'segmented';
};

type ProcessingShape = Partial<Record<ProcessingStep, StatusEntry | null | undefined>>;

// Stages that only run AFTER segmentation. A segmented parent forks into child
// segment-pages and is terminal — these never run on it (they stay undefined
// forever), so their dots are suppressed rather than shown as perpetual pending.
const DOWNSTREAM_OF_SEGMENTATION: ProcessingStep[] = [
  'pdfParser',
  'metadata',
  'embeddings',
  'classification',
  'reconstruction',
  'dataExtraction',
];

interface Props {
  processing: ProcessingShape | null | undefined;
  steps: ProcessingStep[];
}

export function ProcessingStatusCells({ processing, steps }: Props) {
  // A segmented parent page never continues past segmentation; its downstream
  // stages are undefined forever. Suppress those dots so the row doesn't show
  // perpetual gray "pending" for work that will never run. Mirrors the
  // segmentationBlocksDownstream guard in ProcessingStatusCard /
  // DocumentPageProcessingSidebar. Keyed on 'segmented' only (a page-level
  // terminal state) so document-level rollups here are unaffected.
  const isSegmentedParent = processing?.segmentation?.status === 'segmented';
  return (
    <div className="flex space-x-1">
      {steps.map((step) => {
        // Segmentation is flag-gated and runs first; a page that never went through
        // it (ENABLE_SECTION_SEGMENTATION off) has no segmentation stage at all, so
        // omit the dot rather than render a perpetual gray "pending".
        if (step === 'segmentation' && !processing?.segmentation) return null;
        if (isSegmentedParent && DOWNSTREAM_OF_SEGMENTATION.includes(step)) return null;
        return <StatusIndicator key={step} title={STEP_LABELS[step]} status={processing?.[step]?.status} />;
      })}
    </div>
  );
}
